import { Checkbox, TextField } from '@mui/material';
import { GridRenderEditCellParams } from '@mui/x-data-grid';

import { TargetAttributeDto, TargetAttributeDtoType } from '@/generated/model';
import RemoteListEditor from './RemoteListEditor';
import VehicleTypeEditor from './VehicleTypeEditor';
import { ChangeEvent } from 'react';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { getLocale } from '../common/translateFunctions';
import { useTranslation } from 'react-i18next';
import { getTargetAttributeDateTimestamp } from './TargetAttributeRowState';

export default function TypeBasedEditor({ params }: { params: GridRenderEditCellParams }) {
  // the value is the valueGetter transformed value
  const { id, field, row, value, api } = params;
  const targetAttribute: TargetAttributeDto = row;
  const { i18n } = useTranslation();

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    api.setEditCellValue({ id, field, value: value.trim() === '' ? '' : value });
  };

  const handleIntegerChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    const parsed = value === '' ? undefined : parseInt(value, 10);
    if (parsed !== undefined && parsed < 0) return;
    api.setEditCellValue({ id, field, value: Number.isNaN(parsed) ? undefined : parsed });
  };

  const handleDecimalChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    const parsed = value === '' ? undefined : parseFloat(value);
    if (parsed !== undefined && parsed < 0) return;
    api.setEditCellValue({ id, field, value: Number.isNaN(parsed) ? undefined : parsed });
  };

  const handleBooleanChange = (event: ChangeEvent<HTMLInputElement>) => {
    api.setEditCellValue({ id, field, value: event.target.checked });
  };

  const handleDateChange = (value: Date | null) => {
    api.setEditCellValue({ id, field, value: getTargetAttributeDateTimestamp(value) });
  };
  switch (targetAttribute.type) {
    case TargetAttributeDtoType['@string']:
      return <TextField className="target-attribute-value" value={value ?? ''} onChange={handleChange} autoFocus />;
    case TargetAttributeDtoType['@int']:
    case TargetAttributeDtoType['@long']:
      return <NumericTargetAttribute value={value} handleChange={handleIntegerChange} />;
    case TargetAttributeDtoType['@double']:
      return <NumericTargetAttribute value={value} handleChange={handleDecimalChange} />;
    case TargetAttributeDtoType['@boolean']:
      return <Checkbox className="target-attribute-value" checked={Boolean(value)} onChange={handleBooleanChange} />;
    case TargetAttributeDtoType['@date']: {
      const locale = getLocale(i18n.language);
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
          <DateTimePicker
            className="target-attribute-value"
            value={value ? new Date(Number(value)) : null}
            onChange={handleDateChange}
          />
        </LocalizationProvider>
      );
    }
    case TargetAttributeDtoType['@list']:
      return <RemoteListEditor params={params} />;
    case TargetAttributeDtoType['@class']:
      return <VehicleTypeEditor params={params} />;
    default:
      return <TextField className="target-attribute-value" disabled value={value || ''} />;
  }
}

function NumericTargetAttribute({
  value,
  handleChange,
}: {
  value: number;
  handleChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}) {
  return (
    <TextField
      className="target-attribute-value"
      type="number"
      value={value ?? ''}
      onChange={handleChange}
      autoFocus
      slotProps={{
        htmlInput: { min: 0 },
      }}
    />
  );
}
